// Cookie Consent Banner - GDPR Compliant
// Gestisce il consenso cookie con preferenze granulari

import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription,
  DialogFooter 
} from './ui/dialog';
import { Cookie, Settings, Shield, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Genera o recupera session ID per tracking anonimo
const getSessionId = (): string => {
  let sessionId = localStorage.getItem('gdpr_session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('gdpr_session_id', sessionId);
  }
  return sessionId;
};

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface CookieConsentBannerProps {
  onConsentGiven?: (preferences: CookiePreferences) => void;
}

const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({ onConsentGiven }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkConsentStatus();
  }, []);

  const checkConsentStatus = async () => {
    try {
      const sessionId = getSessionId();
      const response = await fetch(`${API}/gdpr/consent/cookies/${sessionId}`);
      const data = await response.json();
      
      if (data.has_consented) {
        setPreferences(data.preferences);
        setShowBanner(false);
      } else {
        setShowBanner(true);
      }
    } catch (error) {
      // Se errore, mostra il banner
      setShowBanner(true);
    } finally {
      setLoading(false);
    }
  };

  const saveConsent = async (prefs: CookiePreferences) => {
    try {
      const sessionId = getSessionId();
      await fetch(`${API}/gdpr/consent/cookies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          preferences: prefs,
          user_agent: navigator.userAgent
        })
      });
      
      setPreferences(prefs);
      setShowBanner(false);
      setShowSettings(false);
      
      // Callback opzionale
      if (onConsentGiven) {
        onConsentGiven(prefs);
      }
      
      // Salva anche in localStorage per accesso rapido
      localStorage.setItem('cookie_consent', JSON.stringify(prefs));
      
    } catch (error) {
      console.error('Errore salvataggio consenso:', error);
    }
  };

  const acceptAll = () => {
    saveConsent({
      necessary: true,
      analytics: true,
      marketing: true
    });
  };

  const acceptNecessary = () => {
    saveConsent({
      necessary: true,
      analytics: false,
      marketing: false
    });
  };

  const saveCustom = () => {
    saveConsent(preferences);
  };

  if (loading || !showBanner) {
    return null;
  }

  return (
    <>
      {/* Banner principale */}
      <div 
        className="fixed bottom-0 left-0 right-0 z-50 bg-card border-t border-border shadow-lg animate-in slide-in-from-bottom duration-300"
        data-testid="cookie-banner"
      >
        <div className="max-w-7xl mx-auto p-4 lg:p-6">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            {/* Icona e testo */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Cookie className="h-5 w-5 text-primary" />
                <h3 className="font-semibold text-foreground">Utilizziamo i Cookie</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Utilizziamo cookie per migliorare la tua esperienza, analizzare il traffico e personalizzare i contenuti.
                Puoi scegliere quali cookie accettare.{' '}
                <Link to="/cookie-policy" className="text-primary hover:underline">
                  Leggi la Cookie Policy
                </Link>
              </p>
            </div>

            {/* Pulsanti */}
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSettings(true)}
                className="gap-2"
                data-testid="cookie-settings-btn"
              >
                <Settings className="h-4 w-4" />
                Personalizza
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={acceptNecessary}
                data-testid="cookie-reject-btn"
              >
                Solo Necessari
              </Button>
              <Button
                size="sm"
                onClick={acceptAll}
                className="gap-2"
                data-testid="cookie-accept-all-btn"
              >
                <Shield className="h-4 w-4" />
                Accetta Tutti
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Dialog impostazioni dettagliate */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="sm:max-w-lg" scrollable>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cookie className="h-5 w-5" />
              Impostazioni Cookie
            </DialogTitle>
            <DialogDescription>
              Gestisci le tue preferenze sui cookie. I cookie necessari sono sempre attivi.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Cookie Necessari */}
            <div className="flex items-start justify-between p-4 bg-muted/50 rounded-lg">
              <div className="flex-1 pr-4">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-medium">Cookie Necessari</h4>
                  <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                    Sempre attivi
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Essenziali per il funzionamento del sito. Includono autenticazione, sicurezza e preferenze base.
                </p>
              </div>
              <Switch checked={true} disabled className="data-[state=checked]:bg-primary" />
            </div>

            {/* Cookie Analitici */}
            <div className="flex items-start justify-between p-4 border border-border rounded-lg">
              <div className="flex-1 pr-4">
                <h4 className="font-medium mb-1">Cookie Analitici</h4>
                <p className="text-sm text-muted-foreground">
                  Ci aiutano a capire come utilizzi il sito per migliorarlo. I dati sono aggregati e anonimi.
                </p>
              </div>
              <Switch
                checked={preferences.analytics}
                onCheckedChange={(checked) => setPreferences({ ...preferences, analytics: checked })}
                data-testid="cookie-analytics-switch"
              />
            </div>

            {/* Cookie Marketing */}
            <div className="flex items-start justify-between p-4 border border-border rounded-lg">
              <div className="flex-1 pr-4">
                <h4 className="font-medium mb-1">Cookie di Marketing</h4>
                <p className="text-sm text-muted-foreground">
                  Permettono di mostrarti contenuti e offerte personalizzate in base ai tuoi interessi.
                </p>
              </div>
              <Switch
                checked={preferences.marketing}
                onCheckedChange={(checked) => setPreferences({ ...preferences, marketing: checked })}
                data-testid="cookie-marketing-switch"
              />
            </div>

            {/* Link alle policy */}
            <div className="pt-2 space-y-1">
              <Link 
                to="/privacy-policy" 
                className="flex items-center text-sm text-primary hover:underline"
              >
                <ChevronRight className="h-4 w-4" />
                Leggi l'Informativa Privacy
              </Link>
              <Link 
                to="/cookie-policy" 
                className="flex items-center text-sm text-primary hover:underline"
              >
                <ChevronRight className="h-4 w-4" />
                Leggi la Cookie Policy
              </Link>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={acceptNecessary}>
              Solo Necessari
            </Button>
            <Button onClick={saveCustom} data-testid="cookie-save-preferences-btn">
              Salva Preferenze
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsentBanner;

// Hook per verificare consensi (da usare in altri componenti)
export const useCookieConsent = () => {
  const [consent, setConsent] = useState<CookiePreferences | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem('cookie_consent');
    if (stored) {
      setConsent(JSON.parse(stored));
    }
  }, []);

  return {
    consent,
    hasAnalytics: consent?.analytics ?? false,
    hasMarketing: consent?.marketing ?? false,
    hasConsented: consent !== null
  };
};
